import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeatureDetailsModal = ({ feature, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!feature) return null;

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: 'Eye' },
    { id: 'technical', label: 'Technique', icon: 'Code' },
    { id: 'timeline', label: 'Chronologie', icon: 'Clock' },
    { id: 'team', label: 'Équipe', icon: 'Users' }
  ];

  const mockTechnicalSpecs = {
    architecture: 'Microservices',
    database: 'PostgreSQL + Redis',
    apis: ['REST API', 'WebSocket'],
    frameworks: ['React', 'Node.js', 'Express'],
    deployment: 'Docker + Kubernetes',
    testing: 'Jest + Cypress'
  };

  const mockTimeline = [
    {
      date: '2024-09-01',
      event: 'Début du développement',
      status: 'completed',
      description: 'Initialisation du projet et setup de l\'environnement'
    },
    {
      date: '2024-09-15',
      event: 'Version Alpha',
      status: 'completed',
      description: 'Première version fonctionnelle pour tests internes'
    },
    {
      date: '2024-10-01',
      event: 'Version Beta',
      status: 'in_progress',
      description: 'Tests utilisateurs et corrections de bugs'
    },
    {
      date: '2024-10-15',
      event: 'Release Production',
      status: 'planned',
      description: 'Déploiement en production'
    }
  ];

  const mockTeamMembers = [
    {
      name: 'Marie Dubois',
      role: 'Lead Developer',
      avatar: null,
      contribution: 85
    },
    {
      name: 'Jean Martin',
      role: 'Backend Developer',
      avatar: null,
      contribution: 70
    },
    {
      name: 'Sophie Chen',
      role: 'UI/UX Designer',
      avatar: null,
      contribution: 60
    },
    {
      name: 'Alex Johnson',
      role: 'DevOps Engineer',
      avatar: null,
      contribution: 45
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'in_progress': return 'text-warning bg-warning/10';
      case 'planned': return 'text-accent bg-accent/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error bg-error/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'low': return 'text-success bg-success/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-start space-x-4">
            <div className={`p-3 rounded-lg ${getStatusColor(feature?.status)}`}>
              <Icon name="Layers" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-card-foreground mb-1">
                {feature?.title}
              </h2>
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(feature?.priority)}`}>
                  {feature?.priority?.toUpperCase()}
                </span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(feature?.status)}`}>
                  {feature?.status === 'in_progress' ? 'EN COURS' : 
                   feature?.status === 'completed' ? 'TERMINÉ' : 'PLANIFIÉ'}
                </span>
                <span className="text-sm text-muted-foreground">
                  {feature?.version}
                </span>
              </div>
            </div>
          </div>
          
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-muted/20 border-b border-border">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Progression</span>
            <span className="text-foreground font-medium">{feature?.progress}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                feature?.status === 'completed' ? 'bg-success' :
                feature?.status === 'in_progress' ? 'bg-warning' : 'bg-accent'
              }`}
              style={{ width: `${feature?.progress}%` }}
            ></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-accent text-accent bg-accent/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[400px]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Description</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature?.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-foreground mb-3">Informations Générales</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-sm text-muted-foreground">Équipe responsable</span>
                      <span className="text-sm text-foreground">{feature?.team}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-sm text-muted-foreground">Date de début</span>
                      <span className="text-sm text-foreground">{formatDate(feature?.startDate)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-sm text-muted-foreground">Date de fin prévue</span>
                      <span className="text-sm text-foreground">{formatDate(feature?.endDate)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-muted-foreground">Impact utilisateur</span>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${
                          feature?.impact === 'high' ? 'bg-error' :
                          feature?.impact === 'medium' ? 'bg-warning' : 'bg-success'
                        }`}></div>
                        <span className="text-sm text-foreground capitalize">{feature?.impact}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-foreground mb-3">Dépendances</h4>
                  {feature?.dependencies?.length > 0 ? (
                    <div className="space-y-2">
                      {feature?.dependencies?.map((depId, index) => (
                        <div key={index} className="flex items-center space-x-2 p-2 bg-muted/50 rounded">
                          <Icon name="GitBranch" size={14} className="text-accent" />
                          <span className="text-sm text-foreground">Fonctionnalité #{depId}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Aucune dépendance</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'technical' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Spécifications Techniques</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(mockTechnicalSpecs)?.map(([key, value]) => (
                    <div key={key} className="p-4 bg-muted/20 rounded-lg">
                      <h4 className="font-medium text-foreground mb-2 capitalize">{key?.replace('_', ' ')}</h4>
                      {Array.isArray(value) ? (
                        <div className="flex flex-wrap gap-1">
                          {value?.map((item, index) => (
                            <span key={index} className="px-2 py-1 bg-accent/10 text-accent text-xs rounded">
                              {item}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{value}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-foreground">Chronologie</h3>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border"></div>
                <div className="space-y-4">
                  {mockTimeline?.map((event, index) => (
                    <div key={index} className="relative flex items-start space-x-4">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full border-4 border-background ${getStatusColor(event?.status)} flex items-center justify-center z-10`}>
                        <Icon name={event?.status === 'completed' ? 'Check' : event?.status === 'in_progress' ? 'Clock' : 'Circle'} size={12} />
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-foreground">{event?.event}</h4>
                          <span className="text-sm text-muted-foreground">{formatDate(event?.date)}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{event?.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'team' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-foreground">Équipe Projet</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mockTeamMembers?.map((member, index) => (
                  <div key={index} className="p-4 border border-border rounded-lg">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={20} className="text-accent" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{member?.name}</h4>
                        <p className="text-sm text-muted-foreground">{member?.role}</p>
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-muted-foreground">Contribution</span>
                            <span className="text-foreground font-medium">{member?.contribution}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div 
                              className="h-1.5 bg-accent rounded-full"
                              style={{ width: `${member?.contribution}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Dernière mise à jour: {formatDate(new Date())}
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              Fermer
            </Button>
            {feature?.status !== 'completed' && (
              <Button>
                <Icon name="Edit" size={16} className="mr-2" />
                Modifier
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureDetailsModal;